import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type SyntheticEvent,
} from 'react';
import { nextOpenKey, openKeyAfterClaim } from '../../lib/accordion';

/**
 * Stacked panels open one at a time — the platform's accordion convention
 * (operator, 2026-09-13: "in any interface like a tab where you have an
 * accordion ui, only open one at a time ... do the same across the
 * platform").
 *
 * A group holds the single open key. A panel inside it takes an `id` and
 * opens or closes THROUGH the group: clicking an open panel closes it,
 * clicking another closes the rest (`nextOpenKey`). `defaultOpen` — the
 * overview's deep link into one Configs editor, the Security tab's first
 * group — claims the initial key, first claim wins (`openKeyAfterClaim`).
 * Outside a group a panel keeps its own state exactly as before, so a lone
 * panel in a dialog is unchanged.
 *
 * Two shapes use it: the ONU page's `Collapsible` (a button + a lazily
 * mounted body) through `useCollapsibleItem`, and the native `<details>`
 * stacks (the raw TR-069 tree, the MCP client snippets, the migration
 * groups) through `useDetailsItem`, which hands back the `open` + `onToggle`
 * pair a controlled `<details>` needs — the browser flips the element, the
 * toggle event reports the flip, and the group re-renders the siblings shut.
 *
 * Closing a sibling must never cost an edit: the draft half at the bottom of
 * this file (`useUnsavedDraft` / `useDraftHolder`) is how a shut panel knows
 * to keep a dirty body mounted rather than unmount it.
 */
interface CollapsibleGroupApi {
  openKey: string | null;
  toggle: (id: string) => void;
  claimDefault: (id: string) => void;
}

const GroupContext = createContext<CollapsibleGroupApi | null>(null);

export function CollapsibleGroup({ children }: { children: ReactNode }) {
  const [openKey, setOpenKey] = useState<string | null>(null);
  /**
   * Set once the initial key is claimed OR the operator has clicked — after
   * either, a panel that mounts late with `defaultOpen` (the chassis WAN
   * fallback appears once the device has loaded) must not move the accordion.
   */
  const decided = useRef(false);

  const toggle = useCallback((id: string) => {
    decided.current = true;
    setOpenKey((current) => nextOpenKey(current, id));
  }, []);
  const claimDefault = useCallback((id: string) => {
    // Read the flag BEFORE the updater is queued: React runs updaters later,
    // and reading the ref inside one saw the flag this same call had set.
    const alreadyDecided = decided.current;
    decided.current = true;
    setOpenKey((current) => openKeyAfterClaim(current, alreadyDecided, id));
  }, []);

  const api = useMemo(() => ({ openKey, toggle, claimDefault }), [openKey, toggle, claimDefault]);
  return <GroupContext.Provider value={api}>{children}</GroupContext.Provider>;
}

/**
 * One panel's view of its group — or, with no group above it (or no `id`),
 * its own open/closed state as before.
 */
export function useCollapsibleItem(
  id: string | undefined,
  defaultOpen: boolean,
): { open: boolean; toggle: () => void } {
  const group = useContext(GroupContext);
  const [local, setLocal] = useState(defaultOpen);
  const grouped = group !== null && id !== undefined;
  // Before paint, so a deep-linked panel never flashes shut on its way open.
  // Mount only: `defaultOpen` is an initial value here as it always was.
  useLayoutEffect(() => {
    if (grouped && defaultOpen) group.claimDefault(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const toggle = useCallback(() => {
    if (grouped) group.toggle(id);
    else setLocal((v) => !v);
  }, [grouped, group, id]);
  return { open: grouped ? group.openKey === id : local, toggle };
}

/**
 * The same, as the props a controlled native `<details>` takes. The toggle
 * event is what the browser fires after IT flipped the element (a click on
 * the summary), and also after React flipped it (a sibling opened, so this
 * one's `open` went false) — only the former is a choice, and it is told
 * apart because the element's state then disagrees with the group's.
 */
export function useDetailsItem(
  id: string,
  defaultOpen = false,
): { open: boolean; onToggle: (event: SyntheticEvent<HTMLDetailsElement>) => void } {
  const { open, toggle } = useCollapsibleItem(id, defaultOpen);
  const onToggle = useCallback(
    (event: SyntheticEvent<HTMLDetailsElement>) => {
      if (event.currentTarget.open !== open) toggle();
    },
    [open, toggle],
  );
  return { open, onToggle };
}

/**
 * An edit in progress inside a panel, reported UPWARD so the panel is never
 * unmounted with it. The group closes a panel the moment another opens, and
 * the ONU page's `Collapsible` mounts its body only while open — so a dirty
 * WAN form (a VLAN picked, PPPoE credentials typed, a SaveBar showing Apply)
 * was gone the moment the operator glanced at LAN, with no warning (review,
 * 2026-09-13; before the group the second panel opened beside it). Now a
 * shut panel keeps a dirty body mounted, hidden, and says so on its header.
 *
 * Two halves. `useUnsavedDraft(dirty)` is the form's: every staged editor —
 * `SaveBar` (WAN, LAN, the ACS record), the WLAN editor, a section's
 * `SecurityGroupControls`, the port-forward form, a diagnostic mid-run —
 * calls it with its own dirty flag and nothing else changes for it; with no
 * panel above (a card on a plain page) it is a no-op. `useDraftHolder` is the
 * panel's: it counts the dirty forms beneath its `DraftScope` and, itself a
 * draft to any panel above, bubbles the state up through a nested panel.
 */
type ReportDraft = (id: string, dirty: boolean) => void;

const DraftContext = createContext<ReportDraft | null>(null);

export function useUnsavedDraft(dirty: boolean): void {
  const report = useContext(DraftContext);
  const id = useId();
  useEffect(() => {
    if (!report) return;
    report(id, dirty);
    // Unmounting — applied and gone, or discarded — is the draft ending.
    return () => report(id, false);
  }, [report, id, dirty]);
}

export function useDraftHolder(): { dirty: boolean; report: ReportDraft } {
  const [dirtyIds, setDirtyIds] = useState<ReadonlySet<string>>(() => new Set());
  const report = useCallback<ReportDraft>((id, dirty) => {
    setDirtyIds((prev) => {
      if (prev.has(id) === dirty) return prev;
      const next = new Set(prev);
      if (dirty) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);
  const dirty = dirtyIds.size > 0;
  useUnsavedDraft(dirty);
  return { dirty, report };
}

/** The panel's side of the contract: everything beneath reports to `report`. */
export function DraftScope({ report, children }: { report: ReportDraft; children: ReactNode }) {
  return <DraftContext.Provider value={report}>{children}</DraftContext.Provider>;
}
